import React from 'react';
import { LandingHeading } from '@components/Landing/shared/LandingHeading';
import type { LandingHeadingProps } from '@components/Landing/shared/LandingHeading';
import type { LandingRankedListItem } from '@components/Landing/shared/LandingRankedList/LandingRankedListItem';
import { LandingRankedListItemSkeleton } from '@components/Landing/shared/LandingRankedList/LandingRankedListItemSkeleton';
import { GridCarousel } from '@components/main-components/carousel/GridCarousel';

const SLIDES_PER_PAGE = 1.1;

export function LandingRankedList({
  headingProps,
  items,
  id,
  imageRadius,
  numCols,
  numRows,
  isLoading,
}: {
  headingProps: LandingHeadingProps;
  items: React.ReactElement<typeof LandingRankedListItem>[];
  id: string;
  numCols: number;
  numRows: number;
  isLoading: boolean;
  imageRadius: 'rounded-md' | 'rounded-full';
}) {
  const itemsOrSkeletons = React.useMemo(() => {
    if (isLoading) {
      return [...Array(numCols * numRows).keys()].map(idx => (
        <LandingRankedListItemSkeleton
          imageRadius={imageRadius}
          key={`landing-ranked-list-item-skeleton-${idx}`}
        />
      ));
    } else {
      return items;
    }
  }, [isLoading, items, numCols, numRows, imageRadius]);

  return (
    <div className="px-4 md:px-6">
      <LandingHeading {...headingProps} />
      <div className="-ml-2">
        <div
          className={`hidden grid-flow-col grid-cols-${numCols} grid-rows-${numRows} gap-2 md:grid`}
        >
          {itemsOrSkeletons}
        </div>
        <div className="block md:hidden">
          <GridCarousel
            slides={[...Array(numCols).keys()].map(idx => {
              return {
                id: `landing-ranked-list-slide-${id}-${idx}`,
                slide: <div>{itemsOrSkeletons.slice(idx * numRows, (idx + 1) * numRows)}</div>,
              };
            })}
            columnAmount={SLIDES_PER_PAGE}
            alignSlidesLeft
            ssrSlideWidth={`calc(100% / ${SLIDES_PER_PAGE})`}
            onEndReached={null}
            onInternalActiveIndexChange={null}
            hideArrows={false}
            externalActiveIndex={null}
            spacing={0}
            columnType="default"
          />
        </div>
      </div>
    </div>
  );
}
